/**
 * Currency Hook - Detects user location and provides currency info
 * Uses free IP geolocation API
 */
import { useState, useEffect, createContext, useContext } from 'react';

// Currency configuration
const CURRENCIES = {
  INR: {
    code: 'INR',
    symbol: '₹',
    name: 'Indian Rupee',
    locale: 'en-IN'
  },
  USD: {
    code: 'USD', 
    symbol: '$',
    name: 'US Dollar',
    locale: 'en-US'
  }
};

// Create context
const CurrencyContext = createContext(null);

// Provider component
export const CurrencyProvider = ({ children }) => {
  const [currency, setCurrency] = useState(CURRENCIES.INR);
  const [country, setCountry] = useState('IN');
  const [loading, setLoading] = useState(true);
  const [isIndia, setIsIndia] = useState(true);

  useEffect(() => {
    detectLocation();
  }, []);

  const detectLocation = async () => {
    try {
      // Try multiple free IP APIs for reliability
      let countryCode = 'IN'; // Default to India
      
      try {
        // Primary: ipapi.co (free, HTTPS supported, no key required)
        const response = await fetch('https://ipapi.co/json/', {
          signal: AbortSignal.timeout(3000) // 3 second timeout
        });
        if (response.ok) {
          const data = await response.json();
          countryCode = data.country_code || 'IN';
        }
      } catch (e) {
        // Fallback: ipapi.co
        try {
          const response = await fetch('https://ipapi.co/country/', {
            signal: AbortSignal.timeout(3000)
          });
          if (response.ok) {
            countryCode = (await response.text()).trim() || 'IN';
          }
        } catch (e2) {
          console.log('IP detection failed, defaulting to India');
        }
      }

      const isIndian = countryCode === 'IN';
      setCountry(countryCode);
      setIsIndia(isIndian);
      setCurrency(isIndian ? CURRENCIES.INR : CURRENCIES.USD);
      
      console.log(`🌍 Location detected: ${countryCode}, Currency: ${isIndian ? 'INR' : 'USD'}`);
    } catch (error) {
      console.error('Location detection error:', error);
      // Default to India on error
      setIsIndia(true);
      setCurrency(CURRENCIES.INR);
    } finally {
      setLoading(false);
    }
  };

  // NOTE: No toggle function - currency is determined by IP only
  // This prevents international users from switching to INR to get lower prices

  const value = {
    currency,
    country,
    isIndia,
    loading,
    CURRENCIES
  };

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
};

// Hook to use currency
export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    // Return default values if used outside provider
    return {
      currency: CURRENCIES.INR,
      country: 'IN',
      isIndia: true,
      loading: false,
      CURRENCIES
    };
  }
  return context;
};

// Utility function to format price based on currency
export const formatPrice = (priceINR, priceUSD, isIndia = true) => {
  if (isIndia) {
    return `₹${priceINR.toLocaleString('en-IN')}`;
  }
  return `$${priceUSD}`;
};

// Get price value based on currency
export const getPrice = (priceINR, priceUSD, isIndia = true) => {
  return isIndia ? priceINR : priceUSD;
};

// Get currency symbol
export const getCurrencySymbol = (isIndia = true) => {
  return isIndia ? '₹' : '$';
};

export default useCurrency;
